import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { catchError, firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { User } from '../models/user.model';
import { extractApiErrorMessage } from './api-error.util';

const TOKEN_KEY = 'amero_token';
const USER_KEY = 'amero_user';

interface LoginResponse {
  err: boolean;
  message: string;
  token: string;
  user: User;
}

interface ApiMessageResponse {
  err: boolean;
  message: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiUrl}`;

  readonly currentUser = signal<User | null>(this.readUser());
  private readonly tokenSignal = signal<string | null>(localStorage.getItem(TOKEN_KEY));

  isAuthenticated(): boolean {
    return this.tokenSignal() !== null;
  }

  getToken(): string | null {
    return this.tokenSignal();
  }

  async login(email: string, password: string): Promise<User> {
    const res = await firstValueFrom(
      this.http.post<LoginResponse>(`${this.baseUrl}/login`, { email, password }).pipe(
        catchError((err) => {
          throw new Error(extractApiErrorMessage(err, 'No se pudo iniciar sesión.'));
        }),
      ),
    );
    if (res.err) throw new Error(res.message || 'No se pudo iniciar sesión.');
    this.setSession(res.token, res.user);
    return res.user;
  }

  /** Registers the account. The API does not log the user in automatically. */
  async register(email: string, password: string, rePassword: string, country: string): Promise<string> {
    const res = await firstValueFrom(
      this.http
        .post<ApiMessageResponse>(`${this.baseUrl}/signup`, {
          email,
          password,
          rePassword,
          country,
          eventCode: '',
        })
        .pipe(
          catchError((err) => {
            throw new Error(extractApiErrorMessage(err, 'No se pudo crear la cuenta.'));
          }),
        ),
    );
    if (res.err) throw new Error(res.message || 'No se pudo crear la cuenta.');
    return res.message;
  }

  /** Step 1 of password recovery: the API emails a link containing a security token. */
  async requestPasswordReset(email: string): Promise<string> {
    const res = await firstValueFrom(
      this.http.post<ApiMessageResponse>(`${this.baseUrl}/users/changePassword`, { email }).pipe(
        catchError((err) => {
          throw new Error(extractApiErrorMessage(err, 'No se pudo enviar el correo de recuperación.'));
        }),
      ),
    );
    if (res.err) throw new Error(res.message || 'No se pudo enviar el correo de recuperación.');
    return res.message;
  }

  /** Step 2: verifies the token pasted from the recovery email before allowing a new password. */
  async verifyResetToken(token: string): Promise<void> {
    const res = await firstValueFrom(
      this.http.get<ApiMessageResponse>(`${this.baseUrl}/users/verifyAccount/${encodeURIComponent(token)}`).pipe(
        catchError((err) => {
          throw new Error(extractApiErrorMessage(err, 'El código no es válido o ha expirado.'));
        }),
      ),
    );
    if (res.err) throw new Error(res.message || 'El código no es válido o ha expirado.');
  }

  /** Step 3: sets the new password using the same token verified in step 2. */
  async confirmPasswordReset(token: string, password: string, rePassword: string): Promise<void> {
    const res = await firstValueFrom(
      this.http
        .post<ApiMessageResponse>(`${this.baseUrl}/users/changeNewPassword`, {
          password,
          rePassword,
          validateAccount: token,
        })
        .pipe(
          catchError((err) => {
            throw new Error(extractApiErrorMessage(err, 'No se pudo restablecer la contraseña.'));
          }),
        ),
    );
    if (res.err) throw new Error(res.message || 'No se pudo restablecer la contraseña.');
  }

  logout(): void {
    this.clearSession();
  }

  /** Called by the auth interceptor when the backend reports the session token expired. */
  handleSessionExpired(): void {
    this.clearSession();
  }

  private readUser(): User | null {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? (JSON.parse(raw) as User) : null;
  }

  private setSession(token: string, user: User): void {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    this.tokenSignal.set(token);
    this.currentUser.set(user);
  }

  private clearSession(): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    this.tokenSignal.set(null);
    this.currentUser.set(null);
  }
}
